import PayoutDetailsSection from '@/components/payouts/PayoutDetailsSection';
import PayoutFailureAlert from '@/components/payouts/PayoutFailureAlert';
import PayoutNoteForm from '@/components/payouts/PayoutNoteForm';
import PayoutTransfersTable from '@/components/payouts/PayoutTransfersTable';
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerRoot,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useDownloadPayoutInvoiceAction } from '@/hooks/actions/use-download-payout-invoice-action';
import { usePayoutQuery } from '@/hooks/queries/use-payout-query';
import { Box, Button, Heading, Spinner, Text, VStack } from '@chakra-ui/react';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'iconoir-react';

interface PayoutDetailsDrawerProps {
  payoutId: string;
  onClose: () => void;
}

const PayoutDetailsDrawer: React.FC<PayoutDetailsDrawerProps> = ({ payoutId, onClose }) => {
  const { t } = useTranslation();
  const {
    data: payout,
    isLoading,
    error,
  } = usePayoutQuery(payoutId, ['transfers', 'paymentMethod', 'note']);

  const { downloadInvoice, isDownloading } = useDownloadPayoutInvoiceAction();

  // The API answers whether an invoice exists; the drawer does not decide.
  const canDownloadInvoice = payout?.invoiceReference != null;

  return (
    <DrawerRoot
      open={true}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="md"
      unmountOnExit
      lazyMount
    >
      <DrawerBackdrop />
      <DrawerContent>
        <DrawerHeader>
          {isLoading && (
            <VStack py={8}>
              <Spinner size="lg" />
            </VStack>
          )}
          {error && (
            <VStack py={8}>
              <Text color="red.500">{t('payouts.drawer.loadingError')}</Text>
            </VStack>
          )}
          {payout && (
            <VStack align="start" gap={1}>
              <DrawerTitle fontSize="2xl">{t('payouts.drawer.title')}</DrawerTitle>
            </VStack>
          )}
          <DrawerCloseTrigger onClick={onClose} />
        </DrawerHeader>
        <DrawerBody>
          {payout && (
            <VStack align="start" gap={8} w="full">
              {/* Failure Alert - shown at top when the payment or a transfer did not go through */}
              <PayoutFailureAlert
                status={payout.status}
                fundedAt={payout.fundedAt}
                lastPaymentFailedAt={payout.lastPaymentFailedAt}
                lastPaymentFailureCode={payout.lastPaymentFailureCode}
                closedReason={payout.closedReason}
                transfers={payout.transfers || []}
                involvedAffiliates={payout.involvedAffiliates || []}
              />
              <Box w="full">
                <Heading fontSize="md" mb={3}>
                  {t('payouts.drawer.detailsSection')}
                </Heading>
                <PayoutDetailsSection payout={payout} />
              </Box>
              {/* Invoice Download Button */}
              {canDownloadInvoice && (
                <Button
                  variant="outline"
                  onClick={() => downloadInvoice(payout.id)}
                  loading={isDownloading}
                  w="full"
                >
                  <Download />
                  {t('payouts.invoice.download')}
                </Button>
              )}
              {/* Transfers Section */}
              <Box w="full">
                <Heading fontSize="md" mb={3}>
                  {t('payouts.drawer.transfersSection')}
                </Heading>
                <PayoutTransfersTable
                  payoutId={payout.id}
                  transfers={payout.transfers || []}
                  involvedAffiliates={payout.involvedAffiliates || []}
                />
              </Box>
              {/* Notes Section */}
              <Box w="full">
                <PayoutNoteForm payoutId={payout.id} defaultValue={payout.note ?? ''} />
              </Box>
              {/* Attachments Section - TODO: Add attachments support to PayoutBatchDetail type */}
              {/* <PayoutAttachmentsSection attachments={payout.attachments || []} /> */}
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </DrawerRoot>
  );
};

export default PayoutDetailsDrawer;
